import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { formsAPI } from "../services/api";
import TopHeader from "../components/TopHeader.jsx";
import FormModal from "../components/shared/FormModal.jsx";
import FormsTable from "../components/shared/FormsTable.jsx";
import FormFilters from "../components/shared/FormFilters.jsx";
import FormPagination from "../components/shared/FormPagination.jsx";
import { formsCache } from "../utils/cache";

const FormsDashboard = () => {
   const [searchParams, setSearchParams] = useSearchParams();
   const [forms, setForms] = useState([]);
   const [totalForms, setTotalForms] = useState(0);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [selectedForm, setSelectedForm] = useState(null);
   const [showModal, setShowModal] = useState(false);
   const [isBackgroundRefresh, setIsBackgroundRefresh] = useState(false);

   // Get filters and pagination from URL
   const formTypeFilter = searchParams.get("form_type") || "";
   const conversationIdFilter = searchParams.get("conversation_id") || "";
   const errorStatusFilter = searchParams.get("error_status") || "";
   const page = parseInt(searchParams.get("page")) || 1;
   const limit = parseInt(searchParams.get("limit")) || 30;

   // Update URL when pagination changes
   const updatePagination = (newPage, newLimit) => {
      const newParams = new URLSearchParams(searchParams);
      if (newPage) newParams.set("page", newPage.toString());
      if (newLimit) newParams.set("limit", newLimit.toString());
      setSearchParams(newParams);
   };

   useEffect(() => {
      loadForms();
   }, [formTypeFilter, conversationIdFilter, errorStatusFilter, page, limit]);

   const loadForms = async () => {
      console.log("📝 Forms: Starting data load");

      // Generate cache key based on filters
      const cacheKey = `${formTypeFilter}-${conversationIdFilter}-${errorStatusFilter}-${page}-${limit}`;
      const cachedData = formsCache.getData();

      if (cachedData && cachedData.cacheKey === cacheKey) {
         console.log("📝 Forms: Using cached data");
         setForms(cachedData.forms);
         setTotalForms(cachedData.totalForms || cachedData.forms.length);
         setLoading(false);

         // If cache needs refresh, update in background
         if (formsCache.needsRefresh()) {
            console.log("📝 Forms: Starting background refresh");
            setIsBackgroundRefresh(true);
            await fetchForms();
            setIsBackgroundRefresh(false);
            console.log("📝 Forms: Background refresh complete");
         }
      } else {
         console.log("📝 Forms: No cache available or filters changed, fetching fresh data");
         await fetchForms();
      }
   };

   const fetchForms = () => {
      // Only show loading state if no data is displayed
      if (!forms.length) {
         console.log("📝 Forms: Showing loading state");
         setLoading(true);
      }

      console.log("📝 Forms: Fetching from API");

      // Return the promise chain to allow `await` in `loadForms`
      return formsAPI
         .getForms({
            form_type: formTypeFilter,
            conversation_id: conversationIdFilter,
            error_status: errorStatusFilter,
            page: page,
            limit: limit,
         })
         .then((response) => {
            // Early exit if the request was not successful
            if (!response.success) {
               throw new Error(response.error || "Failed to load forms");
            }

            console.log("📝 Forms: Updating data");

            // Safely handle response.data, defaulting to an empty array
            const responseData = response.data || [];
            const totalCount =
               response.total || response.totalCount || response.count || responseData.length;

            const formData = {
               forms: responseData,
               formTypes: [...new Set(responseData.map((form) => form.form_type))].filter(Boolean),
               totalForms: totalCount,
               cacheKey: `${formTypeFilter}-${conversationIdFilter}-${errorStatusFilter}-${page}-${limit}`,
            };

            // Update cache and state
            formsCache.setData(formData);
            setForms(formData.forms);
            setTotalForms(totalCount);
            setError("");
         })
         .catch((err) => {
            console.error("❌ Forms: Error fetching data:", err);
            setError(err.message || "Failed to load forms");
         })
         .finally(() => {
            setLoading(false);
         });
   };

   const handleFilterSubmit = (e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      const newParams = new URLSearchParams(searchParams);

      // Reset to first page on filter change
      newParams.set("page", "1");

      for (let [key, value] of formData.entries()) {
         if (value.trim() !== "") {
            newParams.set(key, value);
         } else {
            newParams.delete(key);
         }
      }

      setSearchParams(newParams);
   };

   const handleFormTypeChange = (e) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", "1"); // Reset to first page

      if (e.target.value.trim() !== "") {
         newParams.set("form_type", e.target.value);
      } else {
         newParams.delete("form_type");
      }

      setSearchParams(newParams);
   };

   const handleErrorStatusChange = (e) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", "1"); // Reset to first page

      if (e.target.value.trim() !== "") {
         newParams.set("error_status", e.target.value);
      } else {
         newParams.delete("error_status");
      }

      setSearchParams(newParams);
   };

   const clearFilters = () => {
      formsCache.clearCache(); // Clear cache when filters are cleared
      const newParams = new URLSearchParams();
      newParams.set("page", "1");
      newParams.set("limit", limit.toString());
      setSearchParams(newParams);
   };

   const getPageTitle = () => {
      if (errorStatusFilter === "with_errors") return "Forms with Errors";
      if (errorStatusFilter === "without_errors") return "Forms without Errors";
      return "Forms";
   };

   const getPageDescription = () => {
      if (errorStatusFilter === "with_errors") return "Forms that have error reports";
      if (errorStatusFilter === "without_errors") return "Forms without any error reports";
      return "All form submissions";
   };

   const showFormModal = (form) => {
      console.log("showFormModal called with:", form);
      setSelectedForm(form);
      setShowModal(true);
      console.log("Modal state set to true");
   };

   const closeModal = () => {
      setShowModal(false);
      setSelectedForm(null);
   };

   // Handle pagination
   const handlePageChange = (newPage) => {
      if (newPage >= 1) {
         updatePagination(newPage);
         window.scrollTo({ top: 0, behavior: "smooth" });
      }
   };

   const handleLimitChange = (newLimit) => {
      updatePagination(1, newLimit);
   };

   if (loading && !forms.length) {
      return (
         <div className="loading-container">
            <div className="loading-spinner"></div>
         </div>
      );
   }

   return (
      <>
         <TopHeader
            title={getPageTitle()}
            icon="bi bi-file-earmark-text"
            description={getPageDescription()}
            onRefresh={loadForms}
            isRefreshing={isBackgroundRefresh}
            rightActions={
               <div className="d-flex gap-2">
                  <Link to="/dashboard" className="btn btn-light btn-sm">
                     <i className="bi bi-clipboard-data"></i>
                     Dashboard
                  </Link>
               </div>
            }
         />

         <div
            className="container"
            style={{
               display: "flex",
               flexDirection: "column",
               height: "calc(100vh - 80px)",
               marginTop: "7rem",
            }}
         >
            {error && (
               <div className="alert alert-danger error-alert" role="alert">
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <strong>Error:</strong> {error}
               </div>
            )}

            {/* Background refresh indicator */}
            {isBackgroundRefresh && (
               <div className="text-center mb-2">
                  <small className="text-muted">
                     <i className="bi bi-arrow-repeat spin me-1"></i>
                     Refreshing data...
                  </small>
               </div>
            )}

            {/* Filter Bar and Pagination Controls in a sticky header */}
            <div className="forms-dashboard-header">
               <FormFilters
                  formTypeFilter={formTypeFilter}
                  conversationIdFilter={conversationIdFilter}
                  errorStatusFilter={errorStatusFilter}
                  onFormTypeChange={handleFormTypeChange}
                  onConversationIdSubmit={handleFilterSubmit}
                  onErrorStatusChange={handleErrorStatusChange}
                  onClearFilters={clearFilters}
               />

               {/* Results Info and Forms Per Page Selector */}
               {forms.length > 0 && (
                  <FormPagination
                     page={page}
                     limit={limit}
                     totalForms={totalForms}
                     onPageChange={handlePageChange}
                     onLimitChange={handleLimitChange}
                  />
               )}
            </div>

            {/* Forms Table in a scrollable container */}
            <div className="forms-table-container">
               <FormsTable forms={forms} onRowClick={showFormModal} />
               {forms.length === 0 &&
                  (formTypeFilter || conversationIdFilter || errorStatusFilter) && (
                     <div className="text-center mt-3">
                        <button onClick={clearFilters} className="btn btn-primary btn-sm">
                           <i className="bi bi-arrow-clockwise"></i> Clear Filters
                        </button>
                     </div>
                  )}
            </div>
         </div>

         <FormModal selectedForm={selectedForm} showModal={showModal} onClose={closeModal} />
      </>
   );
};

export default FormsDashboard;
